/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import React, { useState, useEffect } from "react";
import { Shield, Eye, CreditCard, History, MoreHorizontal, FileText } from "lucide-react";
import { formatCurrency } from "../../utils/constants";

const ManualPaymentsTable = ({
  payments,
  getStatusBadge,
  onWaivePenalty,
  onViewDetails,
  onUpdatePayment,
  onViewHistory,
  onViewNotes,
}) => {
  const [activeMenu, setActiveMenu] = useState(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (event.target.closest(".action-menu")) return;
      setActiveMenu(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  return (
    <div className="overflow-x-auto min-h-[300px]">
      <table className="w-full text-sm text-left">
        <thead className="bg-accent dark:bg-gray-900/50 text-xs text-gray-400 dark:text-dark-muted uppercase font-semibold">
          <tr>
            <th className="px-6 py-4">Client</th>
            <th className="px-6 py-4">Phone</th>
            <th className="px-6 py-4">Type</th>
            <th className="px-6 py-4">Due Date</th>
            <th className="px-6 py-4">Amount Due</th>
            <th className="px-6 py-4">Paid</th>
            <th className="px-6 py-4">Remaining</th>
            <th className="px-6 py-4">Status</th>
            <th className="px-6 py-4 text-center">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
          {payments.length > 0 ? (
            payments.map((inv) => (
              <tr
                key={inv.id}
                className="hover:bg-accent dark:hover:bg-gray-800 transition-colors group"
              >
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-xs"
                      style={{
                        backgroundColor: inv.client.avatarColor,
                      }}
                    >
                      {inv.client.initials}
                    </div>
                    <div>
                      <span className="font-bold text-text dark:text-dark-text block">
                        {inv.client.name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-dark-muted block">
                        {inv.client.address}
                      </span>
                      {inv.description && (
                        <span
                          className="text-xs text-primary dark:text-primary-light font-medium block mt-1 max-w-[200px] truncate"
                          title={inv.description}
                        >
                          Note: {inv.description}
                        </span>
                      )}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-500 dark:text-dark-muted">
                  {inv.client.phone || "N/A"}
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 bg-accent dark:bg-gray-800 rounded text-xs font-medium text-gray-600 dark:text-dark-muted">
                    {inv.type}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="text-gray-500 dark:text-dark-muted">
                      {inv.displayDueDate}
                    </span>
                    {inv.isOverdue && !inv.penaltyWaived && (
                      <span className="text-xs text-red-500 font-medium mt-1">
                        {inv.daysOverdue} days overdue
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col">
                    <span className="font-bold text-text dark:text-dark-text">
                      {formatCurrency(inv.totalAmountDue)}
                    </span>
                    {inv.penaltyAmount > 0 && !inv.penaltyWaived && (
                      <span className="text-xs text-orange-500">
                        +{formatCurrency(inv.penaltyAmount)} penalty
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 font-medium text-text dark:text-dark-text">
                  {formatCurrency(inv.totalPaid)}
                </td>
                <td className="px-6 py-4 font-bold text-text dark:text-dark-text">
                  {formatCurrency(inv.remainingBalance)}
                </td>
                <td className="px-6 py-4">{getStatusBadge(inv.status)}</td>
                <td className="px-6 py-4 text-center relative action-menu">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveMenu(activeMenu === inv.id ? null : inv.id);
                    }}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-400 hover:text-primary dark:hover:text-primary transition-colors"
                  >
                    <MoreHorizontal size={18} />
                  </button>

                  {activeMenu === inv.id && (
                    <div className="absolute right-8 top-8 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                      {inv.description && (
                        <button
                          onClick={() => {
                            onViewNotes(inv);
                            setActiveMenu(null);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2 border-b border-gray-100 dark:border-dark-border"
                        >
                          <FileText size={16} className="text-primary" /> See Notes
                        </button>
                      )}

                      <button
                        onClick={() => {
                          onViewDetails(inv);
                          setActiveMenu(null);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Eye size={16} /> View Details
                      </button>

                      {inv.payingStatus !== "paid" && (
                        <button
                          onClick={() => {
                            onUpdatePayment(inv);
                            setActiveMenu(null);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <CreditCard size={16} /> Update Payment
                        </button>
                      )}
                      
                      {inv.penaltyAmount > 0 && !inv.penaltyWaived && (
                        <button
                          onClick={() => {
                            onWaivePenalty(inv);
                            setActiveMenu(null);
                          }}
                          className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                        >
                          <Shield size={16} /> Waive Penalty
                        </button>
                      )}
                      
                      <button
                        onClick={() => {
                          onViewHistory(inv);
                          setActiveMenu(null);
                        }}
                        className="w-full text-left px-4 py-2.5 text-sm text-text dark:text-dark-text hover:bg-accent dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <History size={16} /> View History
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="px-6 py-8 text-center">
                <div className="text-gray-400 dark:text-dark-muted">
                  No payments found
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ManualPaymentsTable;
